import React, { useState, useEffect, useMemo } from 'react';
import { FaStar, FaSearch } from 'react-icons/fa';
import { useTable, useSortBy } from 'react-table';
import ReactPaginate from 'react-paginate';
import { Loader } from '../../../components/base/Loader';
import { allTransactionsInfo } from '../../../apiManager/admin/FinancialManagement.js';
import Papa from "papaparse"
import api from '../../../apiManager/api.js';
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Search } from "lucide-react";

export const getFullName = x => x.firstName + " " + x.lastName;

function CustomerTable() {
    const [sortBy, setSortBy] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [error, setError] = useState('');
    const [totalCount, setTotalCount] = useState(10);
    const rowsPerPage = 8;

    useEffect(() => {
        (async function () {
            const res = await api.get(`/job/getAllTransactionsCount`);
            setTotalCount(res.data.count);
        })();
    }, [])

    useEffect(() => {
        const getTransactionsData = async () => {
            try {
                setLoading(true);
                const res = await allTransactionsInfo(currentPage + 1);
                if (!res) setTransactions([])
                else setTransactions(res);
            } catch (error) {
                setError(error.message || "An error occurred while fetching data");
            } finally {
                setLoading(false);
            }
        }
        getTransactionsData();
    }, [currentPage]);


    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
    };



    const columns = useMemo(
        () => [
            {
                accessorKey: "homeowner.username",
                Header: "Homeowner Username",
                Cell: ({ row }) => row.original.homeowner.username || "N/A",
            },
            {
                accessorKey: "homeowner.fullName",
                Header: "Homeowner Full Name",
                Cell: ({ row }) => row.original.homeowner.fullName || "N/A",
            },
            {
                accessorKey: "shoveler.username",
                Header: "Shoveler Username",
                Cell: ({ row }) => row.original.shoveler.username || "N/A",
            },
            {
                accessorKey: "shoveler.fullName",
                Header: "Shoveler Full Name",
                Cell: ({ row }) => row.original.shoveler.fullName || "N/A",
            },
            {
                accessorKey: "serviceTypes",
                Header: "Service Type",
                Cell: ({ row }) => row.original.serviceTypes || "N/A",
            },
            {
                accessorKey: "serviceRequestId",
                Header: "Service Request ID",
                Cell: ({ row }) => row.original.serviceRequestId,
            },
            {
                accessorKey: "payment.offered",
                Header: "$ Offered",
                Cell: ({ row }) => `$${(row.original.payment.offered / 100).toFixed(2)}`,
            },
            {
                accessorKey: "payment.authorized",
                Header: "$ Authorized",
                Cell: ({ row }) => `$${(row.original.payment.authorized / 100).toFixed(2)}`,
            },
            {
                accessorKey: "payment.captured",
                Header: "$ Captured",
                Cell: ({ row }) => `$${(row.original.payment.captured / 100).toFixed(2)}`,
            },
            {
                accessorKey: "payoutStatus",
                Header: "Payout Status",
                Cell: ({ row }) => {
                    console.log(row.original)
                    const status = row.original.payoutStatus;
                    console.log("Row Original", row.original)
                    const statusColor = {
                        paid: "text-green-600",
                        failed: "text-red-600",
                        created: "text-yellow-600",
                        pending: "text-gray-500",
                    };
                    return <span className={statusColor[status] || "text-gray-500"}>{status}</span>;
                },
            },
        ],
        []
    );

    const data = useMemo(() => {
        let filteredData = transactions;
        if (sortBy !== "All") {
            filteredData = filteredData.filter(t => t.payoutStatus === sortBy)
        }
        if (searchTerm) {
            let st = searchTerm.toLowerCase();
            filteredData = filteredData.filter(t =>
                t.homeowner.fullName.toLowerCase().includes(st) ||
                t.homeowner.username.toLowerCase().includes(st) ||
                t.shoveler.username.toLowerCase().includes(st) ||
                t.shoveler.fullName.toLowerCase().includes(st)
            )
        }
        return filteredData;

    }, [transactions, searchTerm, sortBy]);



    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data });

    const exportToCsv = async () => {
        const res = await allTransactionsInfo();
        const transformedData = res.map(t => ({
            'Homeowner Username': t.homeowner?.username || 'N/A',
            'Homeowner Full Name': t.homeowner?.fullName || 'N/A',
            'Shoveler Username': t.shoveler?.username || 'N/A',
            'Shoveler Full Name': t.shoveler?.fullName || 'N/A',
            'Service Type': t.serviceTypes || 'N/A',
            'Service Request ID': t.serviceRequestId,
            '$ Offered': `$${(t.payment?.offered / 100).toFixed(2)}`,
            '$ Authorized': `$${(t.payment?.authorized / 100).toFixed(2)}`,
            '$ Captured': `$${(t.payment?.captured / 100).toFixed(2)}`,
            'Payout Status': t.payoutStatus || 'N/A',
        }));

        const csv = Papa.unparse(transformedData);

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'transactions_data.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }


    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }

    if (!transactions.length) {
        return <p>No transactions available</p>;
    }
    return (

        // table header
        <div className="mt-6 mb-12">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between mb-6">
                <div className="flex flex-col gap-0">
                    <h2 className="text-xl font-bold">All Transactions</h2>
                    <p className="text-sm text-muted-foreground">Financial Data</p>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center w-full lg:w-auto">
                    <div className="relative w-full sm:flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search..."
                            className="pl-10 w-full"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full sm:w-[180px]">
                            <SelectValue placeholder="Filter status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="All">All</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                    </Select>

                    <Button variant="secondary" onClick={exportToCsv} className="w-full sm:w-auto">
                        Export to CSV
                    </Button>
                </div>
            </div>

            {/* table container */}
            <div className='overflow-x-auto'>
                <table {...getTableProps()} className="min-w-full bg-white">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps(column)}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} className="hover:bg-gray-100">
                                    {row.cells.map((cell) => (
                                        <td
                                            {...cell.getCellProps()}
                                            className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* paginator */}
            <ReactPaginate
                className='pagination-container flex justify-end me-32 mt-10 gap-x-5'
                previousLabel={
                    <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                        &lt;
                    </span>
                }
                nextLabel={
                    <span className="flex items-center justify-center px-2 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out">
                        &gt;
                    </span>
                }
                breakLabel={<span className="mx-2 text-gray-600">...</span>}
                breakClassName='break-me'
                pageCount={Math.ceil(totalCount / rowsPerPage)}
                forcePage={currentPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={`flex items-center`}
                pageLinkClassName={`bg-gray-200 px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-300 transition duration-200 ease-in-out`}
                activeLinkClassName={`bg-zinc-900 text-white rounded-md`}
                disabledClassName={`opacity-50 cursor-not-allowed`}
            />

        </div>
    );
}

export default CustomerTable;